import { useEffect } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { TextField, Autocomplete, ListItem } from '@mui/material'
import useGetThemeColor from '@/hooks/useGetThemeColor'
import useGetDataFromForeignKeyAndDependencies from './useGetDataFromForeignKeyAndDependencies'
import { PropertyFieldProps } from './PropertyField'

type PropertySelectProps = PropertyFieldProps

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getValueByPath(obj: Record<string, any>, path: string) {
  return path
    .split('.')
    .slice(1)
    .reduce((acc, key) => acc?.[key], obj)
}

const commonCss = {
  content: '""',
  transition: 'all 0.1s',
  width: 4,
  display: 'inline-block',
  height: '90%',
  position: 'absolute',
  top: '5%',
  left: 0,
}

export default function PropertySelectFromForeignKey({
  propertyId,
  property,
  required,
  shrinkLabel,
  variant,
  dependencyValues,
}: PropertySelectProps) {
  const { field } = useController({ name: propertyId, rules: { required } })
  const { formState, resetField, setValue } = useFormContext()
  const getThemeColor = useGetThemeColor()

  const { errors } = formState

  const { display, foreignKey } = property

  const {
    allData: data,
    isPending,
    disabledIds,
    hasAllDependencies,
  } = useGetDataFromForeignKeyAndDependencies({
    foreignKey,
    dependencyValues,
  })

  const currentValue = data.find(
    (item) => item.id === field.value && !disabledIds.includes(item.id),
  )
  const namePath = display.item?.name_path

  useEffect(() => {
    // If the selected item no longer exists (i.e. the data set changes), reset the value
    // or if no value is selected and autoSelect is enabled, select the first item
    if (
      data?.length > 0 &&
      !currentValue &&
      (field.value || display.autoSelect)
    ) {
      const firstAvailableItem = data.find(
        (item) => !disabledIds.includes(item.id),
      )
      resetField(propertyId, {
        defaultValue:
          // If display.includeNoneOption, the autoSelect should select "None"
          display.autoSelect && !display.includeNoneOption
            ? firstAvailableItem.id
            : '',
      })
    }
  }, [
    data,
    propertyId,
    currentValue,
    resetField,
    display.autoSelect,
    field.value,
    display.includeNoneOption,
    disabledIds,
  ])

  return (
    <Autocomplete
      {...field}
      disabled={isPending || !hasAllDependencies}
      disableCloseOnSelect
      disableClearable={!display.includeNoneOption || !required}
      value={currentValue || null}
      options={data}
      loading={isPending}
      getOptionLabel={(option) =>
        namePath ? getValueByPath(option, namePath) : option.name || option.code
      }
      getOptionDisabled={(option) => disabledIds.includes(option.id)}
      onChange={(_e, value) => {
        setValue(propertyId, value ? value.id : '', { shouldDirty: true })
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          error={!!errors[propertyId]}
          label={display?.label || 'MISSING LABEL'}
          helperText={display.description}
          variant={variant}
          slotProps={{
            htmlInput: {
              ...params.inputProps,
            },
            inputLabel: { shrink: shrinkLabel },
          }}
          sx={
            display.hasColor
              ? {
                  '&::before': {
                    background: getThemeColor(currentValue?.color),
                    borderRadius: 2,
                    ...commonCss,
                  },
                }
              : undefined
          }
        />
      )}
      renderOption={(props, option) => {
        return (
          <ListItem
            {...props}
            key={option.id}
            dense
            sx={
              display.hasColor
                ? {
                    '&::before': {
                      background: getThemeColor(option?.color),
                      borderRadius: 2,
                      ...commonCss,
                    },
                  }
                : undefined
            }
          >
            {namePath
              ? getValueByPath(option, namePath)
              : option.name || option.code}
          </ListItem>
        )
      }}
      noOptionsText={display.no_items ?? 'No items available.'}
    />
  )
}
